// src/components/common/Testimonials.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Chip,
  Paper,
  IconButton,
  Avatar,
  Rating,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ArrowBack, ArrowForward, FormatQuote } from "@mui/icons-material";
import styles from "./Testimonials.module.css";

// Testimonials Data
const testimonialsData = [
  {
    id: 1,
    name: "Sarah Johnson",
    position: "CEO, TechCorp International",
    avatar: "SJ",
    rating: 5,
    text: "Wavelength Consultancy delivered an exceptional mobile app that transformed our business operations. Their team's expertise in both hardware and software integration was impressive. We've seen a 40% increase in efficiency since implementation.",
    date: "March 2025",
  },
  {
    id: 2,
    name: "Dr. Michael Chen",
    position: "Director, MediHealth Systems",
    avatar: "MC",
    rating: 5,
    text: "The Wave2health solution developed by WCS has revolutionized our patient monitoring system. The hardware is reliable, and the software is intuitive. Our patients and staff absolutely love it. Highly recommended!",
    date: "January 2025",
  },
  {
    id: 3,
    name: "Emily Williams",
    position: "CTO, EduGlobal Academy",
    avatar: "EW",
    rating: 4,
    text: "We partnered with Wavelength for our e-learning platform development. Their team understood our requirements perfectly and delivered a scalable solution on time. The ongoing support has been exceptional.",
    date: "November 2024",
  },
  {
    id: 4,
    name: "James O'Brien",
    position: "Operations Manager, TransFast Logistics",
    avatar: "JO",
    rating: 5,
    text: "The custom tracking system developed by WCS has streamlined our entire logistics operation. Real-time tracking and analytics have given us unprecedented visibility into our supply chain. Outstanding work!",
    date: "September 2024",
  },
  {
    id: 5,
    name: "Priya Patel",
    position: "Founder, SmartRetail Group",
    avatar: "PP",
    rating: 5,
    text: "Wavelength's consultancy services helped us identify key areas for digital transformation. Their strategic insights and technical expertise have been invaluable to our growth. We continue to work with them on multiple projects.",
    date: "July 2024",
  },
  {
    id: 6,
    name: "Robert Taylor",
    position: "Head of IT, FinSecure Bank",
    avatar: "RT",
    rating: 4,
    text: "The security solutions provided by WCS are top-notch. Their team conducted thorough assessments and implemented robust systems that have significantly enhanced our security posture. Very professional.",
    date: "May 2024",
  },
];

const Testimonials = () => {
  const theme = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Hooks at top level
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Determine slides to show based on screen size
  let slidesToShow = 2;
  if (isMobile) {
    slidesToShow = 1;
  } else if (isTablet) {
    slidesToShow = 1;
  }

  // Duplicate slides for infinite loop
  const duplicatedSlides = [
    ...testimonialsData,
    ...testimonialsData,
    ...testimonialsData,
  ];

  // Auto-slide
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => prev + 1);
    }, 4000);
    return () => clearInterval(timer);
  }, [isPaused]);

  // Reset when reaching the end
  useEffect(() => {
    if (currentSlide >= testimonialsData.length) {
      setTimeout(() => {
        setCurrentSlide(0);
      }, 50);
    }
  }, [currentSlide]);

  const getCurrentIndex = () => {
    return currentSlide % testimonialsData.length;
  };

  const handlePrev = () => {
    if (currentSlide === 0) {
      setCurrentSlide(testimonialsData.length - 1);
    } else {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleNext = () => {
    setCurrentSlide(currentSlide + 1);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <Box className={styles.testimonialsSection}>
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box className={styles.testimonialsHeader}>
          <Chip
            label="⭐ Client Testimonials"
            className={styles.testimonialsChip}
            size="small"
          />
          <Typography
            variant="h2"
            className={styles.testimonialsTitle}
            sx={{
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            }}
          >
            What Our <span className={styles.highlight}>Clients Say</span>
          </Typography>
          <Typography variant="body1" className={styles.testimonialsSubtitle}>
            Real feedback from our valued clients worldwide
          </Typography>
        </Box>

        {/* Testimonial Slider */}
        <Box className={styles.sliderWrapper}>
          <Box
            className={styles.sliderContainer}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <Box
              className={styles.sliderTrack}
              style={{
                transform: `translateX(-${currentSlide * (100 / slidesToShow)}%)`,
                transition: "transform 500ms ease-in-out",
              }}
            >
              {duplicatedSlides.map((testimonial, index) => (
                <Box key={index} className={styles.slideItem}>
                  <Paper elevation={0} className={styles.testimonialCard}>
                    {/* Quote Icon */}
                    <Box className={styles.quoteIcon}>
                      <FormatQuote sx={{ fontSize: 40 }} />
                    </Box>

                    {/* Rating */}
                    <Box className={styles.ratingWrapper}>
                      <Rating
                        value={testimonial.rating}
                        readOnly
                        precision={0.5}
                      />
                    </Box>

                    {/* Testimonial Text */}
                    <Typography
                      variant="body1"
                      className={styles.testimonialText}
                    >
                      "{testimonial.text}"
                    </Typography>

                    {/* Client Info */}
                    <Box className={styles.clientInfo}>
                      <Avatar className={styles.clientAvatar}>
                        {testimonial.avatar}
                      </Avatar>
                      <Box className={styles.clientDetails}>
                        <Typography
                          variant="subtitle1"
                          className={styles.clientName}
                        >
                          {testimonial.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          className={styles.clientPosition}
                        >
                          {testimonial.position}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Date */}
                    <Typography
                      variant="caption"
                      className={styles.testimonialDate}
                    >
                      {testimonial.date}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Slider Controls */}
          <Box className={styles.sliderControls}>
            <IconButton
              className={styles.sliderArrow}
              onClick={handlePrev}
              aria-label="Previous"
            >
              <ArrowBack />
            </IconButton>
            <Box className={styles.sliderDots}>
              {testimonialsData.map((_, index) => (
                <button
                  key={index}
                  className={`${styles.sliderDot} ${
                    getCurrentIndex() === index ? styles.sliderDotActive : ""
                  }`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </Box>
            <IconButton
              className={styles.sliderArrow}
              onClick={handleNext}
              aria-label="Next"
            >
              <ArrowForward />
            </IconButton>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Testimonials;
